<template>
    <div class="vue-vehicle-modal-wrapper">
        <perfect-scrollbar>
            <div class="modal-header">
                <h2 v-if="item.title" class="title virtual-p font-weight-bold mb-0">
                    {{ item.title }}
                    <template v-if="item.external_id">({{ item.external_id }})</template>
                </h2>

                <p v-if="item.subtitle" class="subtitle mb-0">{{ item.subtitle }}</p>

                <div class="vehicle-image">
                    <img :src="item.images[0]" :alt="item.subtitle">
                </div>

                <Badges
                    v-if="showBadges"
                    alternative
                    has-title
                    :badges="[
                        ...item.fuel_types,
                        ...item.transmission_types,
                        ...item.body_work_types,
                    ]" />

                <button class="close-button mt-1" @click="handleClose">
                    {{ translations.closeText }}
                </button>
            </div>

            <div class="modal-body">
                <aut-vehicle-details :item="item"></aut-vehicle-details>

                <div class="usps-description-wrapper">
                    <aut-youtube-iframe
                        v-if="item.video"
                        :url="item.video"
                        class="vehicle-video mb-5"
                    ></aut-youtube-iframe>

                    <div v-if="item.usps" v-html="item.usps" class="vehicle-usps"></div>

                    <div v-if="item.description" v-html="item.description" class="vehicle-description"></div>
                </div>

                <div class="pricing-button-wrapper">
                    <div class="pricing-information">
                        <div class="item price-per-day" v-if="item.price_per_day && !item.show_week_prices">
                            <div class="label">Dag</div>
                            <div class="price" v-html="generatePriceMarkup(prices.per_day)"></div>
                        </div>

                        <template v-if="item.price_per_weekend && !item.show_week_prices">
                            <div class="item divider"></div>
                            <div class="item price-per-weekend">
                                <div class="label">Weekend</div>
                                <div class="price" v-html="generatePriceMarkup(prices.per_weekend)"></div>
                            </div>
                        </template>

                        <div v-if="!item.show_week_prices" class="item divider"></div>
                        <div class="item price-per-week" v-if="item.price_per_week">
                            <div class="label">1 week</div>
                            <div class="price" v-html="generatePriceMarkup(prices.per_week)"></div>
                        </div>

                        <template v-if="item.price_per_2_weeks && item.show_week_prices">
                            <div class="item divider"></div>
                            <div class="item price-per-2-weeks">
                                <div class="label">2 weken</div>
                                <div class="price" v-html="generatePriceMarkup(prices.per_2_weeks)"></div>
                            </div>
                        </template>

                        <template v-if="item.price_per_3_weeks && item.show_week_prices">
                            <div class="item divider"></div>
                            <div class="item price-per-3-weeks">
                                <div class="label">3 weken</div>
                                <div class="price" v-html="generatePriceMarkup(prices.per_3_weeks)"></div>
                            </div>
                        </template>
                    </div>

                    <div class="free-kilometers-info" v-if="hasFreeKilometersInfo">
                        <div class="free-kilometers-text" v-html="item.free_kilometers_info_text"></div>
                    </div>

                    <div class="vehicle-rent-button d-none d-lg-block" v-if="!hideCtas">
                        <button @click="handleClick(iframe)" class="btn-primary m-0">{{ translations.rentVehicleText }}</button>
                    </div>

                    <div class="meta-information mt-5" v-if="hasMetaInformation">
                        <ul>
                            <li v-if="item.cargo_space"><strong>Laadruimte:</strong> {{ item.cargo_space }}</li>
                            <li v-if="item.loading_weight"><strong>Laadgewicht:</strong> {{ item.loading_weight }}</li>
                            <li v-if="item.dimensions"><strong>Afmetingen:</strong> {{ item.dimensions }}</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="vehicle-rent-button d-lg-none" v-if="!hideCtas">
                <button @click="handleClick(iframe)" class="btn-primary m-0">{{ translations.rentVehicleText }}</button>
            </div>
        </perfect-scrollbar>
    </div>
</template>

<script>
import translations from '../../mixins/translations';
import CurrencyFormatter from '../../mixins/CurrencyFormatter';
import Details from './Details.vue';
import YoutubeIframe from "../YoutubeIframe.vue";
import analytics from "../../mixins/analytics";
import Badges from "./Badges.vue";
import {isEmpty} from "lodash";

export default {
    name: "ModalWrapper",

    mixins: [CurrencyFormatter, translations, analytics],

    components: {
        Badges,
        'aut-vehicle-details': Details,
        'aut-youtube-iframe': YoutubeIframe
    },

    props: {
        id: {
            type: Number
        },
        locationId: {
            type: Number
        },
        item: {
            type: Object
        },
        hideCtas: {
            type: Boolean,
            default: false
        },
        isBusiness: {
            type: Boolean,
            default: false,
        },
        vatPercentage: {
            type: Number,
            required: true,
        },
        iframe: {
            type: Boolean,
            default: false,
        }
    },

    data() {
        return {
            windowWidth: window.innerWidth
        }
    },

    computed: {
        hasSlider() {
            return this.item.images.length > 1;
        },

        hasMetaInformation() {
            return !lodash.isNil(this.item.cargo_space) || !lodash.isNil(this.item.loading_weight) || !lodash.isNil(this.item.dimensions)
        },

        hasFreeKilometersInfo() {
            return !lodash.isNil(this.item.free_kilometers_info_text);
        },

        prices() {
            const vat = drupalSettings.vat_percentage;

            return {
                per_day: this.calculatePrice(this.item.price_per_day, vat),
                per_weekend: this.calculatePrice(this.item.price_per_weekend, vat),
                per_week: this.calculatePrice(this.item.price_per_week, vat),
                per_2_weeks: this.calculatePrice(this.item.price_per_2_weeks, vat),
                per_3_weeks: this.calculatePrice(this.item.price_per_3_weeks, vat),
                discount_price: this.calculatePrice(this.item.discount_price, vat),
            };
        },

        showBadges() {
            return !isEmpty(this.item.fuel_types) || !isEmpty(this.item.transmission_types) || !isEmpty(this.item.body_work_types);
        }
    },

    mounted() {
      this.handleGA4ViewItem();
    },

    methods: {
        handleClose() {
            this.$modal.close();
        },

        handleClick(newTab) {
            this.handleGA4AddToCart();

            let baseUrl = `/reserveren`;

            if(this.isBusiness) {
                baseUrl = baseUrl.concat(`/zakelijk`);
            }

            if (lodash.isNil(this.locationId)) {
                baseUrl = baseUrl + `?vehicleId=${this.id}&vehicleWrapperId=${this.item.vehicle_wrapper_id}`;
            }else{
                baseUrl = baseUrl + `?vehicleId=${this.id}&vehicleWrapperId=${this.item.vehicle_wrapper_id}&locationId=${this.locationId}`;
            }

            if(newTab) {
                window.open(baseUrl);
                return;
            }

            window.location = baseUrl;
        },

        handleGA4ViewItem() {

            let item = {
              'item_id': this.item.id,
              'item_name': this.item.title,
              'item_category': this.item.vehicle_group.label,
              'price': this.prices.per_day,
              'quantity': 1,
            };

            if (!lodash.isNil(this.locationId)) {
              item.location_id = this.locationId;
            }

            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
                'event': 'view_item',
                'ecommerce': {
                    'currency': 'EUR',
                    'value': this.prices.per_day,
                    'items': [item],
                },
            });
        },

        calculatePrice(price, vatPercentage) {
            return (this.isBusiness === true) ? (price / (1 + (vatPercentage / 100))).toFixed(2) : price;
        },

        generatePriceMarkup(price) {
            if (isNaN(price) || price === null || price === undefined) {
                return price;
            }
            else if (Number.isInteger(price)) {
                return price + ",-";
            }

            const priceParts = price.toString().split(".");
            return `${priceParts[0]},<sup>${priceParts[1]}</sup>`;
        }
    }
}
</script>
